export interface Pt { x: number; y: number }
export const EPS = 1e-9;

export function signedArea(p: Pt[]): number {
  let a = 0;
  for (let i = 0; i < p.length; i++) {
    const q = p[i]!, r = p[(i + 1) % p.length]!;
    a += q.x * r.y - r.x * q.y;
  }
  return a / 2;
}
export const area = (p: Pt[]): number => Math.abs(signedArea(p));
export const ensureCCW = (p: Pt[]): Pt[] => (signedArea(p) < 0 ? [...p].reverse() : [...p]);

export function dedupe(p: Pt[]): Pt[] {
  const out: Pt[] = [];
  for (const pt of p) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(pt.x - last.x, pt.y - last.y) > EPS) out.push(pt);
  }
  while (out.length > 1 && Math.hypot(out[0]!.x - out[out.length - 1]!.x, out[0]!.y - out[out.length - 1]!.y) <= EPS) out.pop();
  return out;
}

export function centroid(p: Pt[]): Pt {
  const a = signedArea(p);
  if (Math.abs(a) < EPS) throw new Error("Sıfır alanlı çokgenin ağırlık merkezi tanımsız");
  let cx = 0, cy = 0;
  for (let i = 0; i < p.length; i++) {
    const q = p[i]!, r = p[(i + 1) % p.length]!;
    const cross = q.x * r.y - r.x * q.y;
    cx += (q.x + r.x) * cross;
    cy += (q.y + r.y) * cross;
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

/** Yatay y doğrusunun kenarlarla kesişim x'leri (yarı açık kural: yatay kenarlar sayılmaz). */
export function crossingsAt(p: Pt[], y: number): number[] {
  const xs: number[] = [];
  for (let i = 0; i < p.length; i++) {
    const a = p[i]!, b = p[(i + 1) % p.length]!;
    if ((a.y > y) !== (b.y > y)) xs.push(a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y));
  }
  return xs.sort((u, v) => u - v);
}

export function widthAt(p: Pt[], y: number): { xMin: number; xMax: number } | null {
  const xs = crossingsAt(p, y);
  if (xs.length < 2) return null;
  return { xMin: xs[0]!, xMax: xs[xs.length - 1]! };
}

function clipHalf(p: Pt[], inside: (q: Pt) => boolean, y0: number): Pt[] {
  const out: Pt[] = [];
  const n = p.length;
  for (let i = 0; i < n; i++) {
    const cur = p[i]!, prev = p[(i - 1 + n) % n]!;
    const cIn = inside(cur), pIn = inside(prev);
    const hit = (): Pt => {
      const t = (y0 - prev.y) / (cur.y - prev.y);
      return { x: prev.x + t * (cur.x - prev.x), y: y0 };
    };
    if (cIn) {
      if (!pIn) out.push(hit());
      out.push(cur);
    } else if (pIn) out.push(hit());
  }
  return dedupe(out);
}
export const clipAbove = (p: Pt[], y0: number): Pt[] => clipHalf(p, (q) => q.y >= y0 - EPS, y0);
export const clipBelow = (p: Pt[], y0: number): Pt[] => clipHalf(p, (q) => q.y <= y0 + EPS, y0);
export const clipBand = (p: Pt[], yLow: number, yHigh: number): Pt[] => clipBelow(clipAbove(p, yLow), yHigh);
/** x ∈ [xMin, xMax] şeridinde kırpma (eksenler takas edilip clipBand). */
export function clipXRange(p: Pt[], xMin: number, xMax: number): Pt[] {
  const swap = (q: Pt[]): Pt[] => q.map((v) => ({ x: v.y, y: v.x }));
  return swap(clipBand(swap(p), xMin, xMax));
}

function orient(a: Pt, b: Pt, c: Pt): number { return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x); }
function segmentsIntersect(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c), o2 = orient(a, b, d), o3 = orient(c, d, a), o4 = orient(c, d, b);
  return o1 * o2 < -EPS && o3 * o4 < -EPS;
}

/** Eğik taban (topuk alçak): tek en alt köşeden (topuk) CCW'de önceki köşeye (burun) kenar en çok 1/2 eğimle yükseliyorsa (buildSection ile aynı kural). */
function inclinedBaseAt(p0: Pt[], minY: number): boolean {
  const p = ensureCCW(p0), n = p.length;
  const i = p.findIndex((q) => Math.abs(q.y - minY) < 1e-6);
  const low = p[i]!, toe = p[(i + n - 1) % n]!;
  const dx = low.x - toe.x, dy = toe.y - low.y;
  return dx > 1e-6 && dy > 1e-6 && dy / dx <= 0.5 + 1e-9;
}

/** Hata listesi döner; boş = geçerli. Kurallar: ≥3 nokta, alan>0, kendini kesmez, taban yatay (en az 2 köşe min y'de) ya da topuğa doğru eğik, her kotta tek parça. */

export function validatePolygon(input: Pt[]): string[] {
  const errors: string[] = [];
  const p = dedupe(input);
  if (p.length < 3) return ["Çokgen en az 3 farklı nokta içermeli"];
  if (area(p) < 1e-6) errors.push("Çokgen alanı sıfır");
  const n = p.length;
  for (let i = 0; i < n; i++) for (let j = i + 2; j < n; j++) {
    if (i === 0 && j === n - 1) continue;
    if (segmentsIntersect(p[i]!, p[(i + 1) % n]!, p[j]!, p[(j + 1) % n]!)) { errors.push("Çokgen kenarları birbirini kesiyor"); i = n; break; }
  }
  const minY = Math.min(...p.map((q) => q.y));
  const onBase = p.filter((q) => Math.abs(q.y - minY) < 1e-6);
  if (onBase.length < 2 && !inclinedBaseAt(p, minY)) errors.push("Duvar tabanı yatay olmalı (en alt kotta en az iki köşe) ya da topuğa doğru en çok 1/2 eğimle inmeli");
  const ys = [...new Set(p.map((q) => +q.y.toFixed(9)))].sort((a, b) => a - b);
  for (let i = 0; i + 1 < ys.length; i++) {
    const ymid = (ys[i]! + ys[i + 1]!) / 2;
    if (crossingsAt(p, ymid).length !== 2) { errors.push("Duvar kesiti her kotta tek parça olmalı"); break; }
  }
  return errors;
}
